/**
 * LearningSuite API contracts – ONLY the endpoints used by the DirectLearningSuiteProvisioner
 * (plan/README.md 2.4, ADR 015) and the admin group check. Derived exactly from
 * `packages/integrations/learningsuite-openapi.json` (LearningSuite API 1.27.0); observed
 * behaviour is documented in `specs/research/learningsuite-api.md` (R1, verified 2026-10-01):
 * - GET /members/by-email → 404 `{ message, errorCode: "resource-not-found" }` for unknown emails
 *   (not an error: continue with POST /members).
 * - GET /groups/{id} → 404 (text) for unknown, 500 for malformed IDs → both mean „group invalid“.
 * - Classify only by HTTP status / follow-up requests, never by message texts.
 *
 * Response schemas are tolerant (`looseObject`): unknown fields are kept, and – exactly as in the
 * OpenAPI document – all response properties are optional. Use the `…WithId` variants where the
 * adapter needs an ID; a missing ID is a schema violation (PERMANENT error).
 *
 * Base URL: env LEARNINGSUITE_API_BASE_URL (default https://api.learningsuite.io/api/v1)
 * Auth: header `X-API-KEY`. Rate limit: 120 requests/minute.
 */
import { z } from 'zod';

export const LEARNINGSUITE_API_KEY_HEADER = 'X-API-KEY';
export const LEARNINGSUITE_RATE_LIMIT_PER_MINUTE = 120;

export const LearningSuitePaths = {
  auth: '/auth',
  membersByEmail: '/members/by-email',
  members: '/members',
  addMembersToGroupsSummary: '/add-members-to-groups-summary',
  group: (id: string) => `/groups/${encodeURIComponent(id)}`,
  /** GET (verification by ID, `includeGroups=true`) and PUT (re-enable a disabled user). */
  member: (id: string) => `/members/${encodeURIComponent(id)}`,
} as const;

/** Machine-readable `errorCode` of LearningSuite 404 JSON bodies (R1). */
export const LS_RESOURCE_NOT_FOUND = 'resource-not-found';
export const LsErrorBodySchema = z.looseObject({
  message: z.string().optional(),
  errorCode: z.string().optional(),
});

/**
 * Fine-grained LearningSuite failure type (R1 §6) – stored in
 * `registrations.provisioning_error_code` / `integration_jobs.last_error_code` and shown to admins
 * as „Fehlertyp“ (§26). The coarse API-level ErrorCode is in `ProvisioningResult.errorCode`.
 */
export const LearningSuiteErrorType = {
  LS_TIMEOUT: 'LS_TIMEOUT',
  LS_NETWORK: 'LS_NETWORK',
  LS_RATE_LIMITED: 'LS_RATE_LIMITED',
  LS_SERVER_ERROR: 'LS_SERVER_ERROR',
  LS_AUTH_FAILED: 'LS_AUTH_FAILED',
  LS_FORBIDDEN: 'LS_FORBIDDEN',
  LS_GROUP_NOT_FOUND: 'LS_GROUP_NOT_FOUND',
  LS_USER_NOT_FOUND: 'LS_USER_NOT_FOUND',
  LS_BAD_REQUEST: 'LS_BAD_REQUEST',
  LS_VERIFICATION_FAILED: 'LS_VERIFICATION_FAILED',
  LS_UNEXPECTED_RESPONSE: 'LS_UNEXPECTED_RESPONSE',
} as const;
export type LearningSuiteErrorType =
  (typeof LearningSuiteErrorType)[keyof typeof LearningSuiteErrorType];
export const LearningSuiteErrorTypeSchema = z.enum(LearningSuiteErrorType);

const nullableString = z.string().nullable();

/** components.schemas.User */
export const LsUserSchema = z.looseObject({
  id: z.string().optional(),
  email: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  fullName: z.string().optional(),
  phone: nullableString.optional(),
  roleId: z.string().optional(),
  locale: z.string().optional(),
  emailVerified: z.boolean().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  lastLogin: nullableString.optional(),
  enabled: z.boolean().optional(),
  about: nullableString.optional(),
  position: nullableString.optional(),
  city: nullableString.optional(),
  accessFrom: nullableString.optional(),
  accessTo: nullableString.optional(),
  updatedUserInfoAt: z.string().optional(),
});
export type LsUser = z.infer<typeof LsUserSchema>;
export const LsUserWithIdSchema = LsUserSchema.extend({ id: z.string().min(1) });
export type LsUserWithId = z.infer<typeof LsUserWithIdSchema>;

/** components.schemas.Group */
export const LsGroupSchema = z.looseObject({
  id: z.string().optional(),
  name: z.string().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type LsGroup = z.infer<typeof LsGroupSchema>;
export const LsGroupWithIdSchema = LsGroupSchema.extend({ id: z.string().min(1) });

/** components.schemas.UserWithGroups (allOf User + { groups }) */
export const LsUserWithGroupsSchema = LsUserSchema.extend({
  groups: z.array(LsGroupSchema).optional(),
});
export type LsUserWithGroups = z.infer<typeof LsUserWithGroupsSchema>;
export const LsUserWithGroupsAndIdSchema = LsUserWithGroupsSchema.extend({
  id: z.string().min(1),
});

// GET /auth → 200 { name }
export const LsAuthResponseSchema = z.looseObject({ name: z.string().optional() });
export type LsAuthResponse = z.infer<typeof LsAuthResponseSchema>;

// GET /members/by-email?email=…&includeGroups=true → 200 User | UserWithGroups.
// Behaviour for unknown emails is not documented (verified by R1, specs/research/learningsuite-api.md).
export const LsGetMemberByEmailQuerySchema = z.object({
  email: z.string(),
  includeGroups: z.boolean().optional(),
});
export type LsGetMemberByEmailQuery = z.infer<typeof LsGetMemberByEmailQuerySchema>;
export const LsGetMemberByEmailResponseSchema = LsUserWithGroupsSchema;

// POST /members → 200 User
export const LsCreateMemberRequestSchema = z
  .object({
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    phone: nullableString.optional(),
    about: nullableString.optional(),
    position: nullableString.optional(),
    city: nullableString.optional(),
    password: z.string().optional(),
    /** If true, the user will not receive a welcome email with login instructions. Default false. */
    disableLoginEmail: z.boolean().optional(),
    doNotRequirePasswordChange: z.boolean().optional(),
    locale: z.enum(['de', 'en']).optional(),
    /** If the member already exists, no error is thrown and the existing member is returned. */
    ignoreIfAlreadyExists: z.boolean().optional(),
  })
  .strict();
export type LsCreateMemberRequest = z.infer<typeof LsCreateMemberRequestSchema>;
export const LsCreateMemberResponseSchema = LsUserSchema;

// PUT /add-members-to-groups-summary → 200 { numberOfAddedUserGroupAssignments }
export const LsAddMembersToGroupsRequestSchema = z
  .object({
    userIds: z.array(z.string()),
    groupIds: z.array(z.string()),
    /** If true, the user will not receive an email about new course accesses. Default false. */
    disableAccessNotificationEmail: z.boolean().optional(),
  })
  .strict();
export type LsAddMembersToGroupsRequest = z.infer<typeof LsAddMembersToGroupsRequestSchema>;
export const LsAddMembersToGroupsSummaryResponseSchema = z.looseObject({
  numberOfAddedUserGroupAssignments: z.number().optional(),
});
export type LsAddMembersToGroupsSummaryResponse = z.infer<
  typeof LsAddMembersToGroupsSummaryResponseSchema
>;

// GET /members/{memberId}?includeGroups=true → 200 User | UserWithGroups, 404 resource-not-found
export const LsGetMemberResponseSchema = LsUserWithGroupsSchema;

// PUT /members/{memberId} → 200 User. Used only with `{ enabled: true }` (re-enable, R1 §5.5).
export const LsUpdateMemberRequestSchema = z
  .object({
    enabled: z.boolean().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    phone: z.string().optional(),
    about: z.string().optional(),
    position: z.string().optional(),
    city: z.string().optional(),
    email: z.string().optional(),
    locale: z.enum(['de', 'en']).optional(),
  })
  .strict();
export type LsUpdateMemberRequest = z.infer<typeof LsUpdateMemberRequestSchema>;
export const LsUpdateMemberResponseSchema = LsUserSchema;

// GET /groups/{id}?includeUsers=false → 200 Group | GroupWithUsers, 404 „Group not found“
export const LsGetGroupResponseSchema = LsGroupSchema.extend({
  users: z.array(LsUserSchema).optional(),
});
export type LsGetGroupResponse = z.infer<typeof LsGetGroupResponseSchema>;
